import { useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { stringifyQueryParameters } from './helpers'
import { ClinicianPatientHeader } from 'ui/clinicianScreens/ClinicianPatientHeader'
import { getTimestampEndOfDay, getTimestampStartOfDay } from 'utilities/time'
import { getTabs } from './Tabs/getTabs'
import { PatientContext, RecentDataAvailabilityContext } from 'ui/contexts'
import {
  reFetchEventList,
  usePatientEvents,
  usePatientEventCount,
} from 'ui/screens/Patient/queries'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'
import useRecentAvailability from 'ui/hooks/useRecentAvailability'
import { useTapTestData } from 'ui/hooks/useTapTestData'
import { ContextCollection } from 'ui/components/ContextCollection'
import useSummaryModeContext, {
  SummaryModeContext,
} from 'ui/contexts/SummaryModeContext'
import { DateRangeContext, DATE_RANGE } from 'ui/contexts/DateRangeContext'
import useUiStateContext, { UiStateContext } from 'ui/contexts/UiStateContext'
import { useRecentDataAvailabilityContext } from 'ui/contexts/RecentDataAvailabilityContext'
import MainContentWrapper from 'ui/templates/MainContentWrapper'
import MainContent from 'ui/templates/MainContent'
import ClinicianPatientTabs from './Tabs/ClinicianPatientTabs'
import useBoundStore from 'domains/zustand/store'
import { Stack } from 'ui/baseComponents/Stack'
import TimezoneDropdown from 'ui/clinicianScreens/Patient/TimezoneDropdown'
import { colors } from 'theme/colors'
import { getWeeklyDatesByCalendarWeeks } from 'ui/clinicianScreens/Patient/WeeklyView/helpers'
import { QUERY_PARAMS } from 'ui/clinicianScreens/Patient/Report/consts'

const {
  daily,
  editPatient,
  log,
  overview,
  reportPatient,
  reportClinician,
  summary,
  summaryComparison,
  weeklyView,
} = ROUTE_METADATA

const ClinicianPatient = () => {
  const hideDateJumpAlert = useBoundStore((state) => state.hideDateJumpAlert)
  const { recentAvailabilityData, setRecentAvailabilityData } =
    useRecentDataAvailabilityContext()
  const [shouldExecuteAvailabilityCall, setShouldExecuteAvailabilityCall] =
    useState(true)

  const { id: patientId } = useContext(PatientContext)

  const { dateRanges, setDateRange, selectedTimezone, setSelectedTimezone } =
    useContext(DateRangeContext)
  const { start: startTime, end: endTime } =
    dateRanges[DATE_RANGE.default.key] || {}
  const { start: startTimePeriod1, end: endTimePeriod1 } =
    dateRanges[DATE_RANGE.period1.key] || {}
  const { start: startTimePeriod2, end: endTimePeriod2 } =
    dateRanges[DATE_RANGE.period2.key] || {}
  const { start: reportPrevStartDate, end: reportPrevEndDate } =
    dateRanges[DATE_RANGE.reportPeriod1.key] || {}
  const { start: reportRecentStartDate, end: reportRecentEndDate } =
    dateRanges[DATE_RANGE.reportPeriod2.key] || {}
  const { start: weeklyStartDate, end: weeklyEndDate } =
    dateRanges[DATE_RANGE.weeklyViewPeriod.key] || {}

  const { pathname } = useLocation()
  const llmSummaryQueryKey = QUERY_PARAMS.includeLLMSummaries
  const [searchParams, setSearchParams] = useSearchParams()
  const includeLLMSummariesQuery = searchParams.get(llmSummaryQueryKey)

  const PATHNAMES = {
    comparisonMode: `/patients/${patientId}/${summaryComparison.path}`,
    dailyView: `/patients/${patientId}/${daily.path}`,
    detailView: `/patients/${patientId}/${summary.path}`,
    editPatient: `/patients/${patientId}/${editPatient.path}`,
    overview: `/patients/${patientId}/${overview.path}`,
    patientLog: `/patients/${patientId}/${log.path}`,
    reportClinician: `/patients/${patientId}/${reportClinician.path}`,
    reportPatient: `/patients/${patientId}/${reportPatient.path}`,
    weeklyView: `/patients/${patientId}/${weeklyView.path}`,
  }

  const startTimeForEventRequests = getTimestampStartOfDay(
    startTime,
    selectedTimezone,
  )
  const endTimeForEventRequests = getTimestampEndOfDay(
    endTime,
    selectedTimezone,
  )

  const { loadingEvents, errorEvents, dataEvents, fetchMore } =
    usePatientEvents(
      patientId,
      startTimeForEventRequests,
      endTimeForEventRequests,
    )

  const {
    tapTests,
    loading: loadingTapTests,
    error: errorTapTests,
  } = useTapTestData(
    patientId,
    startTimeForEventRequests,
    endTimeForEventRequests,
  )

  const { eventCountLoading, eventCountError, eventCountData } =
    usePatientEventCount(
      patientId,
      startTimeForEventRequests,
      endTimeForEventRequests,
    )

  const eventList = useMemo(() => {
    const events = dataEvents?.patient?.eventList?.events || []

    // tapTests are already in event format from useTapTestData
    // Merge and sort by start time (newest first)
    return [...events, ...tapTests].sort(
      (a, b) => b.duration.startTime - a.duration.startTime,
    )
  }, [dataEvents, tapTests])

  useEffect(() => {
    const topSymptomEventCounts =
      eventCountData?.patient?.eventCount?.counts || []

    useBoundStore.setState(() => ({
      eventList,
      topSymptomEventCounts,
      loadingEvents,
      loadingTapTests,
      eventCountError,
      eventCountLoading,
      errorEvents,
      errorTapTests,
    }))
  }, [
    eventList,
    loadingEvents,
    loadingTapTests,
    eventCountError,
    eventCountLoading,
    errorEvents,
    errorTapTests,
    eventCountData?.patient?.eventCount?.counts,
  ])

  /*  -------------------------
      CONTEXT VALUES
  -------------------------  */

  const uiStateContextValue = useUiStateContext()

  const summaryModeContextValue = useSummaryModeContext()
  const { comparisonModeEnabled, setComparisonModeEnabled } =
    summaryModeContextValue

  const recentDataAvailabilityContextValue = useRecentAvailability(
    startTime,
    endTime,
    shouldExecuteAvailabilityCall,
  )

  if (
    !recentAvailabilityData &&
    Object.values(recentDataAvailabilityContextValue?.data).some((val) => val)
  ) {
    setRecentAvailabilityData(recentDataAvailabilityContextValue)
  }

  if (recentAvailabilityData && shouldExecuteAvailabilityCall) {
    setShouldExecuteAvailabilityCall(false)
  }

  const patientDataContexts = [
    { context: UiStateContext, value: uiStateContextValue },
    { context: SummaryModeContext, value: summaryModeContextValue },
    { context: RecentDataAvailabilityContext, value: recentAvailabilityData },
  ]
  /*  -------------------------
      END CONTEXT VALUES
  -------------------------  */

  /*  -------------------------
      USE EFFECTS
  -------------------------  */
  useEffect(() => {
    hideDateJumpAlert()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  useEffect(() => {
    // (XXX Elina): TODO -- replace with useSearchParams hook
    const searchParams = new URLSearchParams(window.location.search)
    const startTimeFromSearchParams = searchParams.get('startTime')
    const endTimeFromSearchParams = searchParams.get('endTime')
    const startTimePeriod1FromSearchParams =
      searchParams.get('startTimePeriod1')
    const endTimePeriod1FromSearchParams = searchParams.get('endTimePeriod1')
    const startTimePeriod2FromSearchParams =
      searchParams.get('startTimePeriod2')
    const endTimePeriod2FromSearchParams = searchParams.get('endTimePeriod2')

    // If the user navigates directly to the summaryComparison route, we set comparisonModeEnabled to true
    if (pathname === PATHNAMES.comparisonMode && !comparisonModeEnabled) {
      setComparisonModeEnabled(true)
    }

    /*  STANDARD DATE CHECK - Routes with special date handling or no dates are excluded from this check and handled below
        ------------------------------
        URL search params are our source of truth.
        If they exist, ensure that the date range stored in our context matches.
        Otherwise (if no URL search params), set them based on what is stored in our context.
    */
    if (
      pathname !== PATHNAMES.comparisonMode &&
      pathname !== PATHNAMES.editPatient &&
      pathname !== PATHNAMES.reportClinician &&
      pathname !== PATHNAMES.reportPatient &&
      pathname !== PATHNAMES.weeklyView
    ) {
      if (
        startTimeFromSearchParams &&
        endTimeFromSearchParams &&
        startTime !== startTimeFromSearchParams &&
        endTime !== endTimeFromSearchParams
      ) {
        setDateRange(startTimeFromSearchParams, endTimeFromSearchParams)
      } else {
        const newSearchParams = stringifyQueryParameters(startTime, endTime)
        setSearchParams(newSearchParams, {
          replace: true,
        })
      }
    }

    /*  WEEKLY VIEW DATE CHECK
        ------------------------------
        URL search params are our source of truth.
        If they exist, ensure that the date range stored in our context matches.
        Otherwise (if no URL search params), set them based on what is stored in our context.
    */
    if (pathname === PATHNAMES.weeklyView) {
      const { adjustedStartDate } = getWeeklyDatesByCalendarWeeks(weeklyEndDate)

      if (!startTimeFromSearchParams || !endTimeFromSearchParams) {
        const newSearchParams = stringifyQueryParameters(
          reportPrevStartDate,
          reportRecentEndDate,
        )
        setSearchParams(newSearchParams, {
          replace: true,
        })
        setDateRange(
          reportPrevStartDate,
          reportRecentEndDate,
          DATE_RANGE.weeklyViewPeriod.key,
        )
      } else if (
        startTimeFromSearchParams &&
        endTimeFromSearchParams &&
        weeklyStartDate !== startTimeFromSearchParams &&
        weeklyEndDate !== endTimeFromSearchParams
      ) {
        setDateRange(
          adjustedStartDate,
          weeklyEndDate,
          DATE_RANGE.weeklyViewPeriod.key,
        )
      } else if (adjustedStartDate !== weeklyStartDate) {
        const newSearchParams = stringifyQueryParameters(
          adjustedStartDate,
          weeklyEndDate,
        )
        setSearchParams(newSearchParams, {
          replace: true,
        })
      }
    }

    /*  COMPARISON MODE DATE CHECK
        --------------------------
        URL search params are our source of truth.
        If they exist, ensure that the date range stored in our context matches.
        Otherwise (if no URL search params), set them based on what is stored in our context.
    */
    if (pathname === PATHNAMES.comparisonMode) {
      if (
        startTimePeriod1FromSearchParams &&
        endTimePeriod1FromSearchParams &&
        startTimePeriod2FromSearchParams &&
        endTimePeriod2FromSearchParams &&
        startTimePeriod1 !== startTimePeriod1FromSearchParams &&
        endTimePeriod1 !== endTimePeriod1FromSearchParams &&
        startTimePeriod2 !== startTimePeriod2FromSearchParams &&
        endTimePeriod2 !== endTimePeriod2FromSearchParams
      ) {
        setDateRange(
          startTimePeriod1FromSearchParams,
          endTimePeriod1FromSearchParams,
          DATE_RANGE.period1.key,
        )
        setDateRange(
          startTimePeriod2FromSearchParams,
          endTimePeriod2FromSearchParams,
          DATE_RANGE.period2.key,
        )
      } else {
        const summaryComparisonSearchParams =
          startTimePeriod1 &&
          endTimePeriod1 &&
          startTimePeriod2 &&
          endTimePeriod2
            ? {
                startTimePeriod1,
                endTimePeriod1,
                startTimePeriod2,
                endTimePeriod2,
              }
            : ''
        setSearchParams(summaryComparisonSearchParams, { replace: true })
      }
    }

    /*  REPORT DATE CHECK
        --------------------------
        URL search params are our source of truth.
        If they exist, ensure that the date range stored in our context matches.
        Otherwise (if no URL search params), set them based on what is stored in our context.
    */
    if (
      pathname === PATHNAMES.reportClinician ||
      pathname === PATHNAMES.reportPatient
    ) {
      if (
        startTimePeriod1FromSearchParams &&
        endTimePeriod1FromSearchParams &&
        startTimePeriod2FromSearchParams &&
        endTimePeriod2FromSearchParams &&
        startTimePeriod1 !== startTimePeriod1FromSearchParams &&
        endTimePeriod1 !== endTimePeriod1FromSearchParams &&
        startTimePeriod2 !== startTimePeriod2FromSearchParams &&
        endTimePeriod2 !== endTimePeriod2FromSearchParams
      ) {
        setDateRange(
          startTimePeriod1FromSearchParams,
          endTimePeriod1FromSearchParams,
          DATE_RANGE.reportPeriod1.key,
        )
        setDateRange(
          startTimePeriod2FromSearchParams,
          endTimePeriod2FromSearchParams,
          DATE_RANGE.reportPeriod2.key,
        )
      } else {
        const reportsSearchParams =
          reportRecentStartDate &&
          reportRecentEndDate &&
          reportPrevStartDate &&
          reportPrevEndDate
            ? {
                startTimePeriod1: reportPrevStartDate,
                endTimePeriod1: reportPrevEndDate,
                startTimePeriod2: reportRecentStartDate,
                endTimePeriod2: reportRecentEndDate,
              }
            : {}

        if (includeLLMSummariesQuery) {
          reportsSearchParams[llmSummaryQueryKey] = 'True'
        }

        setSearchParams(reportsSearchParams, { replace: true })
        setDateRange(
          reportPrevStartDate,
          reportPrevEndDate,
          DATE_RANGE.reportPeriod1.key,
        )
        setDateRange(
          reportRecentStartDate,
          reportRecentEndDate,
          DATE_RANGE.reportPeriod2.key,
        )
      }
    }

    /*  PATIENT SETTINGS DATE OVERRIDE
        --------------------------
        When the user navigates to the editPatient route, clear the dates from the URL params.
    */
    if (pathname.startsWith(PATHNAMES.editPatient)) {
      setSearchParams('', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // The hook to continue fetching data in the instance that multiple "pages" would need querying
  useEffect(() => {
    // We need there to be data returned to then get the endCursor to try for other pages worth of data
    const { endCursor } = dataEvents?.patient?.eventList?.pageInfo || {}
    if (endCursor) {
      reFetchEventList(fetchMore, endCursor)
    }
  }, [dataEvents, fetchMore])

  /*  -------------------------
      END USE EFFECTS
  -------------------------  */

  const showTimezoneDropdown =
    pathname === PATHNAMES.comparisonMode ||
    pathname === PATHNAMES.dailyView ||
    pathname === PATHNAMES.detailView ||
    pathname === PATHNAMES.reportClinician ||
    pathname === PATHNAMES.overview ||
    pathname === PATHNAMES.patientLog ||
    pathname === PATHNAMES.reportPatient ||
    pathname === PATHNAMES.weeklyView

  return (
    <>
      <ClinicianPatientHeader getTabs={getTabs} />
      <ContextCollection contexts={patientDataContexts}>
        <MainContentWrapper>
          <MainContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ backgroundColor: colors.COOL[100] }}
              data-cy="tab-container"
            >
              <ClinicianPatientTabs getTabs={getTabs} />
              {showTimezoneDropdown && (
                <TimezoneDropdown
                  timezone={selectedTimezone}
                  updateTimezone={setSelectedTimezone}
                />
              )}
            </Stack>
            <Outlet />
          </MainContent>
        </MainContentWrapper>
      </ContextCollection>
    </>
  )
}

export default ClinicianPatient
